import { Box, Flex } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../theme/colors';
import { formatAgeGroupForDisplay } from '../../utils/ageGroup';
import { hexToRgbA } from '../../utils/helper';
import { saveCasesListItems, saveCasesListState } from '../../utils/casesListState';
import { Body, Headline } from '../Texts/Texts';
import SeverityTag from './SeverityTag';
import SymptomsTag from './SymptomsTag';

const getCaseStatusText = (stage: string, status?: string) => {
  if (status === 'REFERRED') {
    return 'Referred';
  }
  switch (stage) {
    case 'ACTIVE':
      return 'Active';
    case 'QUESTION_COMPLETED':
      return 'Question Completed';
    case 'ONGOING':
      return 'Ongoing';
    case 'CLOSED':
      return 'Closed';
    default:
      return stage || 'Unknown';
  }
};

const getCaseStatusColor = (stage: string, status?: string) => {
  if (status === 'REFERRED') {
    return colors.deep_blue;
  }
  switch (stage) {
    case 'ACTIVE':
      return colors.accent;
    case 'QUESTION_COMPLETED':
      return colors.warning;
    case 'ONGOING':
      return colors.deep_blue;
    case 'CLOSED':
      return colors.error;
    default:
      return colors.gray;
  }
};

interface CaseCardProps {
  data: any;
  index: string | number;
  items?: any;
  listState?: {
    page?: number;
    tab?: string;
    filter?: string;
    pageSize?: number;
    search?: string;
  };
}

const CaseCard = ({ data, index, items, listState }: CaseCardProps) => {
  const navigate = useNavigate();
  const caseType = () => {
    switch (data.severity.toLowerCase()) {
      case 'moderate':
        return {
          bg: hexToRgbA(colors.accent, '0.1')
        };
      case 'moderately_severe':
        return {
          bg: hexToRgbA(colors.warning, '0.1')
        };
      case 'severe':
        return {
          bg: hexToRgbA(colors.error, '0.1')
        };
      default:
        return {
          bg: hexToRgbA(colors.gray, '0.1')
        };
    }
  };
  return (
    <Box
      bg={caseType().bg}
      p='8px 8px 12px 16px'
      borderRadius='8px'
      cursor='pointer'
      onClick={() => {
        if (listState) {
          saveCasesListState(listState);
        }
        if (items?.length) {
          saveCasesListItems(items);
        }
        navigate(`/cases/${data._id}`, {
          state: { items, index, ...listState }
        });
      }}
    >
      <Flex direction='column' align='flex-end'>
        <SeverityTag type={data.severity} mb='4px' />
        <Body color='gray' size='12px'>
          {new Date(data.createdAt).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            year: 'numeric'
          })}
        </Body>
      </Flex>
      <Headline color='deep_blue' size='19px' fontWeight={500} mb='4px'>
        {data.user?.name ?? 'N/A'}
      </Headline>
      <Body color='gray' size='14px' mb='8px'>
        {formatAgeGroupForDisplay(data.user?.ageGroup)} •{' '}
        {data.user?.gender ?? 'N/A'}
      </Body>
      <Flex alignItems='center' gap='8px' mb='8px'>
        <Box
          px='8px'
          py='4px'
          borderRadius='4px'
          bg={getCaseStatusColor(data.stage, data.status) + '20'}
          border={`1px solid ${getCaseStatusColor(data.stage, data.status)}`}
        >
          <Body
            size='10px'
            fontWeight={600}
            color={getCaseStatusColor(data.stage, data.status)}
          >
            {getCaseStatusText(data.stage, data.status)}
          </Body>
        </Box>
      </Flex>
      <Flex>
        {/* {data.labels.map((label: string, index: number) => (
          <SymptomsTag text={label} mr="8px" key={label + index} />
        ))} */}
        <SymptomsTag text={data?.symptom ?? 'N/A'} mr='8px' />
      </Flex>
    </Box>
  );
};

export default CaseCard;
